import { isFailure, isOk, isPending, Result } from "@/common/common";
import { TMessage } from "@/i18n/tt";
import { DCCallback, useCallbackHandler, useTranslation } from "@/stores/RootStoreContext";
import { classNames } from "@/utils/classNames";
import { handleEnter } from "@/utils/keyboardEvents";
import { Dialog as BaseDialog } from "@base-ui/react/dialog";
import { ReactElement, ReactNode } from "react";
import { Alert } from "./Alert";
import { Box } from "./Box";
import { Button, ButtonProps, IconButton } from "./Button";
import { IconProps } from "./Icon";
import { IconName } from "./iconMapping";
import { spacingClassName } from "./tailwindMappings";

function toMaxWidthClassName(maxWidth: Required<DialogProps>["maxWidth"]): string {
  switch (maxWidth) {
    case "xs":
      return "max-w-120";
    case "sm":
      return "max-w-160";
    case "md":
      return "max-w-220";
    case "lg":
      return "max-w-280";
    case "xl":
      return "max-w-360";
  }
}

function toHeightClassName(height: DialogProps["height"] = "auto") {
  switch (height) {
    case "sm":
      return "h-120";
    case "md":
      return "h-[40em]";
    case "lg":
      return "h-200";
    case "full":
      return "h-full";
  }
}

export type DialogProps = {
  title?: string | TMessage;
  subHeaderElement?: ReactElement;
  onSubmit?: DCCallback;
  isSubmitDisabled?: boolean;
  isSubmitting?: boolean;
  submitLabel?: string | TMessage;
  submitIcon?: IconName | IconProps;
  submitColor?: ButtonProps["color"];
  onCancel?: DCCallback;
  isCancelDisabled?: boolean;
  cancelLabel?: string | TMessage;
  height?: "auto" | "sm" | "md" | "lg" | "full";
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
  children?: ReactNode;
  actions?: ReactNode;
  fullscreen?: boolean;
  overlay?: boolean;
  submitResult?: Result<TMessage, TMessage>;
  padding?: "sm" | "md" | "lg";
  showAlert?: boolean;
  headerElement?: ReactNode;
  alertPosition?: "top" | "bottom";
  isSubDialog?: boolean; // Increase z-index (needed for some nested dialogs)
};

export const Dialog = ({
  title,
  subHeaderElement,
  children,
  maxWidth = "md",
  height = "auto",
  isCancelDisabled,
  cancelLabel,
  submitLabel,
  submitIcon,
  isSubmitting,
  isSubmitDisabled,
  actions,
  fullscreen,
  overlay,
  submitResult,
  padding,
  showAlert,
  headerElement,
  alertPosition,
  ...props
}: DialogProps) => {
  const { t } = useTranslation();
  const submitting = isSubmitting || isPending(submitResult);
  const displayAlert = showAlert ?? true;
  const onSubmit = useCallbackHandler(props.onSubmit);
  const onCancel = useCallbackHandler(props.onCancel);
  const renderAlert = () => {
    if (displayAlert && isOk(submitResult)) {
      return <Alert type={"success"} message={<>{t(submitResult.data)}</>} />;
    }
    if (displayAlert && isFailure(submitResult)) {
      return <Alert type={"error"} message={<>{t(submitResult.error)}</>} />;
    }
    return null;
  };
  const bottomAlert = alertPosition === "bottom" ? renderAlert() : null;

  return (
    <BaseDialog.Root
      open
      onOpenChange={(open) => {
        if (!open && !isCancelDisabled && !submitting) {
          onCancel?.();
        }
      }}
    >
      <BaseDialog.Portal>
        <BaseDialog.Backdrop className="fixed inset-0 backdrop" forceRender />
        <div className="fixed inset-0 flex justify-center items-center p-4">
          <BaseDialog.Popup
            className={classNames(
              "flex-1",
              "flex",
              "flex-col",
              "max-h-full",
              "rounded-sm",
              "bg-default",
              "shadow-2xl",
              "overflow-hidden",
              padding && spacingClassName("p", padding),
              !fullscreen && !overlay && toMaxWidthClassName(maxWidth),
              !fullscreen && !overlay && toHeightClassName(height),
              fullscreen && ["absolute", "inset-2", "sm:inset-10"],
              overlay && ["absolute", "inset-0"],
            )}
          >
            {(title || onCancel) && (
              <div className={"flex items-center gap-4 px-4 pt-2"}>
                {title && (
                  <BaseDialog.Title className={"my-2 text-h4"}>
                    {typeof title === "string" ? title : t(title)}
                  </BaseDialog.Title>
                )}
                <Box className={"flex-1"}>{headerElement}</Box>
                {onCancel && !isCancelDisabled && (
                  <BaseDialog.Close
                    disabled={isCancelDisabled || submitting}
                    render={(closeProps) => (
                      <IconButton
                        {...closeProps}
                        aria-hidden
                        tabIndex={-1}
                        icon={{ name: "x", weight: "bold" }}
                        tooltip={t("close")}
                        color={"inherit"}
                      />
                    )}
                  />
                )}
              </div>
            )}
            {subHeaderElement}
            <form
              onSubmit={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onSubmit?.();
              }}
              className={classNames("flex flex-col flex-1 overflow-y-auto pb-6 p-4")}
              onKeyDown={
                // Prevent submitting the dialog with enter - not relevant on overlay dialogs (which are not really used as dialogs)
                overlay
                  ? handleEnter((event) => {
                      if (["INPUT"].includes((event.target as HTMLElement).tagName)) {
                        event.preventDefault();
                      }
                    })
                  : undefined
              }
            >
              {alertPosition !== "bottom" && renderAlert()}
              {children}
            </form>
            {bottomAlert && <div className={"px-4 pb-2"}>{bottomAlert}</div>}
            {actions ? (
              <div className={"px-4 pb-4"}>{actions}</div>
            ) : (
              <div
                className={"px-4 pb-4 flex flex-col gap-2 justify-end md:flex-row md:justify-end"}
              >
                {onCancel && (
                  <BaseDialog.Close
                    disabled={isCancelDisabled || submitting}
                    render={(buttonProps) => (
                      <Button
                        {...(buttonProps as ButtonProps)}
                        className={"min-w-40"}
                        data-testid={"onCancel"}
                        variant={"secondary"}
                        label={cancelLabel ?? (isOk(submitResult) ? t("close") : t("cancel"))}
                      />
                    )}
                  />
                )}
                {onSubmit && !isOk(submitResult) && (
                  <Button
                    data-testid={"dialogButton"}
                    className={"min-w-40"}
                    variant={"primary"}
                    icon={submitIcon}
                    label={submitLabel ?? { key: "submit" }}
                    loading={submitting}
                    onClick={onSubmit}
                    disabled={isSubmitDisabled}
                    color={props.submitColor}
                  />
                )}
              </div>
            )}
          </BaseDialog.Popup>
        </div>
      </BaseDialog.Portal>
    </BaseDialog.Root>
  );
};
